import {
  Box,
  Flex,
  Grid,
  GridItem,
  Text,
} from '@chakra-ui/react'
import React from 'react'
import {
  deviceGroupSumByProperty,
  addCommasToNumber,
  calculatePercentage,
} from '../../../../../utils/helpers'
import { getShadeFromPercent } from '../../../../../utils/colors'
import { EnergyConsumptionReport, IDevice } from '../../../api/types'

type Props = {
  locations: EnergyConsumptionReport
  data: any
}

function isConsumption(location: any) {
  return deviceGroupSumByProperty(location.children, 'value') > 0
}

function getGroupDevicePercentage(location: any, device: IDevice): number {
  const property = isConsumption(location) ? "value" : "generated_value";

  const part = typeof device?.[property] === "number" ? device[property] : 0;
  const whole = typeof location?.value === "number" ? location.value : 0;

  return calculatePercentage(part, whole, 1);
}

const EnergyReportBody = ({ locations, data }: Props) => {
  const locationsWithStats = locations
    .map((loc) => {
      const percent = (loc.value / data.currConsumption) * 100 || 0
      const color = getShadeFromPercent('#157fee', percent)
      return { ...loc, percent, color }
    })
    .sort((a, b) => b.percent - a.percent)

  const isGroupedByLocation = locations?.[0]?.location_id !== undefined

  return (
    <Flex direction="column" w="100%" px={10} mb={10} gap={8}>
      {/* Overview bar */}
      <Box
        rounded="xl"
        bg="white"
        px="20px"
        py="16px"
        className="consumption-by-location"
        boxShadow="sm"
        mt={5}
      >
        <Text fontWeight={700} fontSize="14px" mb={3}>Energy Consumption by {isGroupedByLocation ? "Location" : "Group"}</Text>
        {locationsWithStats.map((loc) => (
          <Grid
            key={loc?.location_id || loc?.group_id}
            templateColumns="110px 1fr 80px"
            alignItems="center"
            gap={4}
            mb={3}
            px={3}
          >
            <Text
              fontWeight={700}
              fontSize="14px"
              color="#707070"
              overflowWrap="break-word"
              wordBreak="break-word"
              whiteSpace="normal"
            >
              {loc.title}
            </Text>
            <Box
              position="relative"
              height="56px"
              width="100%"
              bg="#f0f0f0"
              borderRadius="md"
              overflow="hidden"
            >
              {loc.percent > 0 && (
                <Box
                  bg={loc.color}
                  height="100%"
                  width={`${loc.percent}%`}
                  transition="width 0.3s ease"
                />
              )}
            </Box>

            <Flex direction="column" alignItems="center" justifyContent="center">
              <Text fontWeight="700" fontSize="14px">{Math.round(loc.percent)}%</Text>
              <Text ml={2} color="gray.500" fontSize="12px" display="flex" gap={1}>
                {loc.value.toLocaleString()}<span>kWh</span>
              </Text>
            </Flex>
          </Grid>
        ))}
      </Box>

      {/* Detailed breakdown per location */}
      {locations.map((location) => {
        return (
          <Box key={location.location_id || location.group_id} className='locations' rounded="lg" bg="white" px={6} py={6} boxShadow="sm">
            <Text fontSize="16px" fontWeight="700" mb={4}>
              {location.title}
            </Text>

            {/* Table Header */}
            <Grid
              templateColumns="1fr 1fr 1fr"
              alignItems="center"
              mb={2}
              mt={2}
              pr={2}
              pl={0}
            >
              <Text fontSize="13px" fontWeight="700" color="#9B9B9B">
                Device
              </Text>
              <Text fontSize="13px" fontWeight="700" color="#9B9B9B" textAlign="right">
                Percentage
              </Text>
              <Text fontSize="13px" fontWeight="700" color="#9B9B9B" textAlign="right">
                Cost for consumption
              </Text>
            </Grid>

            {/* Devices */}
            {location.children.map((device) =>
              <Box key={device.id} mb={1}>
                <DeviceRow device={device} location={location} indent={0} />
                {device.children?.length > 0 &&
                  renderDeviceChildrenRecursive(device.children, location, 1)}
              </Box>
            )}

            {/* Total Row */}
            <Grid
              templateColumns="1fr 1fr 1fr"
              alignItems="center"
              py={2}
              fontWeight="bold"
            >
              <Text fontSize="14px" color="gray.700"></Text>
              <Text></Text>
              <Text fontSize="14px" textAlign="right" color="gray.700">
                {addCommasToNumber(location.value)} kWh
              </Text>
            </Grid>
          </Box>
        )
      })}
    </Flex>
  )
}

type DeviceRowProps = {
  device: any
  location: any
  indent: number
}

const DeviceRow = ({ device, location, indent }: DeviceRowProps) => {
  const percent = getGroupDevicePercentage(location, device)

  return (
    <Grid
      templateColumns="1fr 1fr 1fr"
      alignItems="center"
      py={2}
      borderBottom="1px solid #f1f1f1"
    >
      <GridItem>
        <Text
          fontWeight={400}
          fontSize="14px"
          color="#484848"
          pl={`${indent * 20}px`}
        >
          {device.title}
        </Text>
      </GridItem>
      <GridItem>
        <Text fontSize="14px" fontWeight="medium" textAlign="right">
          {percent}%
        </Text>
      </GridItem>
      <GridItem>
        <Text fontSize="14px" fontWeight="700" color="#707070" textAlign="right">
          {addCommasToNumber(device.value)}<span style={{ marginLeft: "5px", fontWeight: 400 }}>kWh</span>
        </Text>
      </GridItem>
    </Grid>
  )
}

function renderDeviceChildrenRecursive(
  children: any[],
  location: any,
  indent: number
): JSX.Element {
  return (
    <>
      {children.map((child) => (
        <Box key={child.device_id}>
          <DeviceRow device={child} location={location} indent={indent} />
          {child.children?.length > 0 &&
            renderDeviceChildrenRecursive(child.children, location, indent + 1)}
        </Box>
      ))}
    </>
  )
}

export { EnergyReportBody }
